import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Grid, Box } from '@mui/material';
import { Typography } from '@totalsoft/rocket-ui';
import { loadFinancialProducts } from '../../actionCreators';
import SimulatorFinancialProductComponent from './SimulatorFinancialProductComponent';
import { withTranslation } from 'react-i18next';
import useDebounce from '../../../../utils/hooks/useDebounce';
import WrappedFakeText from 'components/common/WrappedFakeText';

const SimulatorFinancialProductsContainer = ({
  t,
  actions,
  products,
  assetInfo,
  partnerTypeId,
  partnerAgreesWithPersonalDataUse,
  savingOffer,
  completed,
  selectedProductId,
  canGenerateContract,
  onGenerateOffer,
  onGenerateContract,
  selfPartnerName,
  userPartnerName
}) => {
  const [productsLoaded, setProductsLoaded] = useState(false);
  const { loaded, request, data } = products;

  const debounceAssetInfo = useDebounce(assetInfo, 500);

  useEffect(() => {
    setProductsLoaded(false);
    actions.loadFinancialProducts({
      assetHierarchyId: debounceAssetInfo.assetHierarchyId,
      duration: debounceAssetInfo.financingPeriod,
      advanceAmount: debounceAssetInfo.advance,
      partnerTypeId: partnerTypeId
    });
  }, [debounceAssetInfo, partnerTypeId, actions]);

  useEffect(() => {
    if (
      loaded &&
      request &&
      request.assetHierarchyId === debounceAssetInfo.assetHierarchyId &&
      request.duration === debounceAssetInfo.financingPeriod &&
      request.advanceAmount === debounceAssetInfo.advance &&
      request.partnerTypeId === partnerTypeId
    ) {
      setProductsLoaded(true);
    }
  }, [loaded, request, data, debounceAssetInfo, partnerTypeId]);

  return (
    <>
      {!productsLoaded ? (
        <WrappedFakeText />
      ) : (
        <>
          <Typography color='primary' sx={{ width: '100%' }} p={1} minHeight={8}>
            {t('Simulator.Disclaimer.Offer')}{' '}
            <Box component='span' fontWeight={'fontWeightBold'}>
              {t(assetInfo.assetHierarchyName)}
            </Box>{' '}
            {t('Simulator.Disclaimer.Available')}{' '}
            <Box component='span' fontWeight={'fontWeightBold'}>
              {userPartnerName}
            </Box>{' '}
            {t('Simulator.Disclaimer.Financed')}{' '}
            <Box component='span' fontWeight={'fontWeightBold'}>
              {selfPartnerName}
            </Box>
          </Typography>
          <Grid container spacing={3} mt={0}>
            {data.map((product, ind) => (
              <SimulatorFinancialProductComponent
                key={ind}
                product={product}
                partnerAgreesWithPersonalDataUse={partnerAgreesWithPersonalDataUse}
                savingOffer={savingOffer}
                completed={completed}
                selectedProductId={selectedProductId}
                canGenerateContract={canGenerateContract}
                onGenerateOffer={onGenerateOffer}
                onGenerateContract={onGenerateContract}
              />
            ))}
          </Grid>
        </>
      )}
    </>
  );
};

SimulatorFinancialProductsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
  assetInfo: PropTypes.object.isRequired,
  partnerTypeId: PropTypes.number.isRequired,
  partnerAgreesWithPersonalDataUse: PropTypes.bool.isRequired,
  savingOffer: PropTypes.bool.isRequired,
  completed: PropTypes.object.isRequired,
  selectedProductId: PropTypes.number,
  canGenerateContract: PropTypes.bool.isRequired,
  onGenerateOffer: PropTypes.func.isRequired,
  onGenerateContract: PropTypes.func.isRequired,
  selfPartnerName: PropTypes.string.isRequired,
  userPartnerName: PropTypes.string.isRequired
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadFinancialProducts
      },
      dispatch
    )
  };
}

function mapStateToProps(state) {
  const products = state.simulatorData.products;
  return {
    products,
    selfPartnerName: state.offerConfig.selfPartnerName,
    userPartnerName: state.userInfo.userExtraInfoModel.userPartnerName
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(SimulatorFinancialProductsContainer);
